import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getContentByHash } from '../services/apiService';
import GlobalLayout from './GlobalLayout';
import './Dashboard.css';

// --- HELPER: Wählt den Inhalt basierend auf der aktuellen Sprache ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  
  // 1. Priorität: Gewählte Sprache (z.B. name_de)
  const localizedField = `${field}_${code}`;
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }

  // 2. Priorität: Englisch als Fallback (z.B. name_en)
  const enField = `${field}`;
  if (item[enField] && String(item[enField]).trim() !== '') {
    return item[enField];
  }

  // 3. Priorität: Default Feld (z.B. name)
  return item[field] || '';
};

const Favourites = () => {
  const navigate = useNavigate();
  const { getFavourites, toggleFavourites } = useAuth();
  
  // Sprache holen
  const { t, currentLanguage } = useLanguage();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleRemoveFavorite = async (hashId, event) => {
    event.stopPropagation(); // Prevent card click
    try {
      await toggleFavourites(hashId);
      
      // Refresh the favorites list
      const response = await getFavourites();
      
      if (response && response.status === 'success' && Array.isArray(response.data)) {
        if (response.data.collections || response.data.elements) {
             const cols = response.data.collections || [];
             const els = response.data.elements || [];
             setItems([...cols, ...els]);
        } else {
             setItems(response.data);
        }
      } else if (Array.isArray(response)) {
        setItems(response);
      } else if (response && Array.isArray(response.items)) {
        setItems(response.items);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Error removing favorite:', err);
      setError(t('failedToRemoveFavorite') || 'Failed to remove favorite');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await getFavourites();
        
        let rawItems = [];
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          if (response.data.collections || response.data.elements) {
             const cols = response.data.collections || [];
             const els = response.data.elements || [];
             rawItems = [...cols, ...els];
          } else {
             rawItems = response.data;
          }
        } else if (Array.isArray(response)) {
          rawItems = response;
        } else if (response && Array.isArray(response.items)) {
          rawItems = response.items;
        } 
        
        setItems(rawItems);
      } catch (err) {
        console.error('Error loading favourites:', err);
        setError(t('failedToLoadFavourites') || 'Failed to load favourites');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [getFavourites, currentLanguage]); // Reload bei Sprachwechsel

  const openFavourite = async (id) => {
    try {
      const hashId = String(id || '').trim();
      if (!hashId) return;

      if (hashId.startsWith('el-')) {
        navigate(`/element/${hashId}`, { state: { sourcePage: '/favourites' } });
        return;
      }
      if (hashId.startsWith('col-')) {
        navigate(`/course/${hashId}`, { state: { sourcePage: '/favourites' } });
        return;
      }

      const content = await getContentByHash(hashId);
      if (content?.type === 'element') {
        navigate(`/element/${hashId}`, { state: { sourcePage: '/favourites', elementData: content.data?.data } });
      } else if (content?.type === 'collection') {
        navigate(`/course/${hashId}`, { state: { sourcePage: '/favourites', collectionData: content.data?.data } });
      } else {
        setError(t('failedToLoadElement') || 'Failed to load element');
      }
    } catch (e) {
      console.error('Open favourite failed:', e);
      setError(t('failedToLoadElement') || 'Failed to load element');
    }
  };

  return (
    <GlobalLayout title={t('favorites') || "Favorites"}>
      {loading && (
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>{t('loadingFavorites') || "Loading favorites..."}</p>
        </div>
      )}

      {!loading && error && (
        <div className="error-section">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="favorites-grid">
          {items.length === 0 && (
            <div className="favorites-empty-state">
              <p>{t('noFavoritesYet') || "No favorites yet."}</p>
            </div>
          )}
          {items.map((item, idx) => {
            // --- HIER: Übersetzung anwenden ---
            const localizedTitle = getLocalizedContent(item, 'name', currentLanguage) || item.name || item.title || 'Untitled';
            const localizedDesc = getLocalizedContent(item, 'description', currentLanguage) || item.description || (t('favoriteItem') || 'Favorite item');
            
            // Image URL fix
            let image = item.url_thumbnail || item.image;
            if (image && image.includes('app.hejvi.de/img/') && !image.includes('/api/img/')) {
               image = image.replace('app.hejvi.de/img/', 'app.hejvi.de/api/img/');
            }

            return (
            <div
              key={item.hash_id || item.id || idx}
              className="favorite-card"
              onClick={() => {
                const id = item.hash_id || item.id;
                if (id) openFavourite(id);
              }}
            >
              <div className="favorite-card-content">
                <div className="favorite-card-header">
                  <h3 className="favorite-card-title">{localizedTitle}</h3>
                  <button
                    className="favorite-remove-button"
                    onClick={(e) => handleRemoveFavorite(item.hash_id || item.id, e)}
                    title={t('removeFromFavorites') || "Remove from favorites"}
                  >
                    ×
                  </button>
                </div>
                <p className="favorite-card-description">
                  {localizedDesc}
                </p>
                {image && (
                  <div className="favorite-card-thumbnail">
                    <img src={image} alt={localizedTitle} />
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </GlobalLayout>
  );
};

export default Favourites;